import { HttpClient, HttpHeaders } from "@angular/common/http";
import { computed, Injectable, signal } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { Observable, of, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";

const helper = new JwtHelperService();

// Storage key constants
const STORAGE_KEYS = {
    ACCESS_TOKEN: "access_token",
    EXPIRES_AT: "expires_at",
    REFRESH_TOKEN: "refresh_token",
    ORIGINAL_ACCESS_TOKEN: "original_access_token",
    ORIGINAL_EXPIRES_AT: "original_expires_at",
    ORIGINAL_REFRESH_TOKEN: "original_refresh_token",
    MFA_ACCESS_TOKEN: "mfa_access_token",
    MFA_EXPIRES_AT: "mfa_expires_at",
    DEMO_MODE: "demo_mode"
};

type TokenResponse = {
    accessToken: string;
    expiresAt: string;
    refreshToken: string;
    isPreAuthorization?: boolean;
};

type IAuthClaims = {
    accountIds: string;
    accountName: string;
    exp: number;
    firstName: string;
    lastName: string;
    userId: number;
    phone: string;
    email: string;
    role: string[] | string;
    departmentTypes?: string[] | string;
    claimsAccount?: string[] | string;
    servicesAccount?: string[] | string;
};

/* eslint-disable @typescript-eslint/naming-convention */
export enum Role {
    Unknown = "UNKNOWN",
    ISIAdministration = "ISI Administration",
    ISIAccounting = "ISI Accounting",
    ISICustomerService = "ISI Customer Service",
    ISIStaff = "ISI Staff",
    ISIClaims = "ISI Claims",
    AgencyRepresentative = "Agency Representative",
    LenderAdministration = "Lender Administration",
    LenderClaims = "Lender Claims",
    LenderAccounting = "Lender Accounting",
    LenderViewOnly = "Lender View Only"
}

export type Roles = `${Role}`;
/* eslint-enable @typescript-eslint/naming-convention */

const API_URL = environment.api;

@Injectable()
export class AuthService {
    public claimsAccounts = signal<string[]>([]);
    public showClaims = computed(
        () => this.claimsAccounts().length > 0 || this.isIsiUser
    );

    public servicesAccounts = signal<string[]>([]);
    public showServices = computed(
        () => this.servicesAccounts().length > 0 || this.isIsiUser
    );

    #claims: IAuthClaims | null = null;
    #initialImpersonation = true;
    #roles: Role[] | null = null;

    constructor(public http: HttpClient) {}

    get isAgencyRep() {
        return this.roles.includes(Role.AgencyRepresentative);
    }

    get isDemoMode() {
        return window.sessionStorage.getItem(STORAGE_KEYS.DEMO_MODE) ?
                true
            :   false;
    }

    set isDemoMode(value: boolean) {
        if (value) {
            window.sessionStorage.setItem(STORAGE_KEYS.DEMO_MODE, "true");
        } else {
            window.sessionStorage.removeItem(STORAGE_KEYS.DEMO_MODE);
        }
    }

    get isImpersonating(): boolean {
        return window.localStorage.getItem(STORAGE_KEYS.ORIGINAL_ACCESS_TOKEN) ?
                true
            :   false;
    }

    get isPreAuthorization() {
        return window.sessionStorage.getItem(STORAGE_KEYS.MFA_ACCESS_TOKEN) ?
                true
            :   false;
    }

    get isViewOnly() {
        return this.roles.includes(Role.LenderViewOnly);
    }

    get isIsiUser() {
        return (
            this.roles.includes(Role.ISIStaff) ||
            this.roles.includes(Role.ISIAdministration) ||
            this.roles.includes(Role.ISIStaff) ||
            this.roles.includes(Role.ISIClaims)
        );
    }

    get roles(): Role[] {
        if (this.#roles) {
            return this.#roles;
        } else if (this.claims && this.claims.role) {
            return this.#roles;
        } else {
            return [];
        }
    }

    get accessToken(): string {
        return window.localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    }

    get refreshToken(): string {
        return window.localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    }

    get claims(): IAuthClaims {
        const jwt = window.localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (!jwt) {
            return null;
        }

        if (this.#claims === null) {
            const t = helper.decodeToken(jwt);
            if (t) {
                this.#claims = {
                    accountIds: t.AccountId,
                    claimsAccount: t.ClaimsAccount,
                    servicesAccount: t.ServicesAccount,
                    exp: t.exp,
                    phone: t[
                        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/otherphone"
                    ],
                    email: t[
                        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
                    ],
                    firstName:
                        t[
                            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
                        ],
                    lastName:
                        t[
                            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"
                        ],
                    accountName: t.AccoutName,
                    userId: t[
                        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
                    ],
                    role: t[
                        "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                    ],
                    departmentTypes: t.DepartmentType
                } as IAuthClaims;
            }
        }
        if (this.#claims && !Array.isArray(this.#claims.role)) {
            const str = this.#claims.role as string;
            if (str) {
                this.#claims.role = str.split(",");
            } else {
                this.#claims.role = [];
            }
        }
        if (this.#claims) {
            this.setRoles(this.#claims.role as string[]);
        } else {
            this.setRoles([]);
        }

        if (this.#claims && !Array.isArray(this.#claims.departmentTypes)) {
            const str = this.#claims.departmentTypes as string;
            if (str) {
                this.#claims.departmentTypes = str.split(",");
            } else {
                this.#claims.departmentTypes = [];
            }
        }

        if (this.#claims) {
            if (!Array.isArray(this.#claims.claimsAccount)) {
                const str = this.#claims.claimsAccount as string;
                if (str) {
                    this.#claims.claimsAccount = str.split(",");
                } else {
                    this.#claims.claimsAccount = [];
                }
            }

            this.claimsAccounts.set(this.#claims.claimsAccount);

            if (!Array.isArray(this.#claims.servicesAccount)) {
                const str = this.#claims.servicesAccount as string;
                if (str) {
                    this.#claims.servicesAccount = str.split(",");
                } else {
                    this.#claims.servicesAccount = [];
                }
            }

            this.servicesAccounts.set(this.#claims.servicesAccount);
        }

        return this.#claims;
    }

    claimsEnabledForAccount(accountId: string): boolean {
        if (this.isIsiUser) {
            return true;
        }

        return this.claimsAccounts().includes(accountId);
    }

    servicesEnabledForAccount(accountId: string): boolean {
        if (this.isIsiUser) {
            return true;
        }

        return this.servicesAccounts().includes(accountId);
    }

    isValid(): { isValid: boolean; reason?: string } {
        const accessToken = window.localStorage.getItem(
            STORAGE_KEYS.ACCESS_TOKEN
        );
        if (!accessToken) {
            // Scenario - User attempts to access the app
            // on first visit (e.g.private browsing window, new computer)
            //
            // note: even going to root of the app gets us here due
            //       to default route in app.router being /app instead
            //       of login. no message is needed here.
            //
            console.log(`auth.service/accessToken`);
            return {
                isValid: false
            };
        }

        const expiresAt = new Date(
            window.localStorage.getItem(STORAGE_KEYS.EXPIRES_AT)
        );
        if (expiresAt < new Date()) {
            // Scenario - User attempts to load the app but their
            // access token has expired (60m duration).
            //
            window.localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            window.localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
            window.localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

            return {
                isValid: false,
                reason: "Browser session has expired."
            };
        }

        return { isValid: true };
    }

    login(email: string, password: string): Observable<TokenResponse> {
        this.logout();

        return this.http
            .post<TokenResponse>(`${API_URL}/api/v1/authorize/login`, {
                email,
                password
            })
            .pipe(tap((res) => this.extractJwt(res)));
    }

    verifyMfa(token: string): Observable<TokenResponse> {
        const mfaAccessToken = window.sessionStorage.getItem(
            STORAGE_KEYS.MFA_ACCESS_TOKEN
        );

        const headers = new HttpHeaders()
            .set("Authorization", `bearer ${mfaAccessToken}`)
            .set("Content-Type", "application/json");

        return this.http
            .post<TokenResponse>(
                `${API_URL}/api/v1/authorize/mfa`,
                JSON.stringify(token),
                { headers }
            )
            .pipe(tap((res) => this.extractJwt(res)));
    }

    impersonate(userId: string): Observable<TokenResponse> {
        const h = new HttpHeaders().set(
            "Authorization",
            `bearer ${this.accessToken}`
        );

        if (!this.isImpersonating) {
            window.localStorage.setItem(
                STORAGE_KEYS.ORIGINAL_ACCESS_TOKEN,
                window.localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
            );
            window.localStorage.setItem(
                STORAGE_KEYS.ORIGINAL_EXPIRES_AT,
                window.localStorage.getItem(STORAGE_KEYS.EXPIRES_AT)
            );
            window.localStorage.setItem(
                STORAGE_KEYS.ORIGINAL_REFRESH_TOKEN,
                window.localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
            );
            this.#initialImpersonation = true;
        } else {
            this.#initialImpersonation = false;
        }

        return this.http
            .post<TokenResponse>(
                `${API_URL}/api/v1/authorize/impersonate`,
                {},
                { headers: h, params: { userId } }
            )
            .pipe(tap((res) => this.extractJwt(res)));
    }

    stopImpersonating(): Observable<IAuthClaims> {
        if (this.isImpersonating) {
            const t = window.localStorage.getItem(
                STORAGE_KEYS.ORIGINAL_ACCESS_TOKEN
            );
            const expires = window.localStorage.getItem(
                STORAGE_KEYS.ORIGINAL_EXPIRES_AT
            );
            const refresh = window.localStorage.getItem(
                STORAGE_KEYS.ORIGINAL_REFRESH_TOKEN
            );

            window.localStorage.removeItem(STORAGE_KEYS.ORIGINAL_ACCESS_TOKEN);
            window.localStorage.removeItem(STORAGE_KEYS.ORIGINAL_EXPIRES_AT);
            window.localStorage.removeItem(STORAGE_KEYS.ORIGINAL_REFRESH_TOKEN);
            this.#initialImpersonation = true;

            this.extractJwt({
                accessToken: t,
                expiresAt: expires,
                refreshToken: refresh
            });
            return of(this.claims); // need to hit the claims() function to restore values, otherwise extractJwt() leaves it null
        } else {
            return throwError(new ErrorEvent("400"));
        }
    }

    refresh(requestUrl: string): Observable<TokenResponse> {
        const h = new HttpHeaders()
            .set("Authorization", `bearer ${this.accessToken}`)
            .set("Content-Type", "application/json");

        return this.http
            .post<TokenResponse>(
                `${API_URL}/api/v1/authorize/refresh`,
                JSON.stringify(this.refreshToken),
                { headers: h }
            )
            .pipe(
                map((res: TokenResponse) => {
                    const jwt = this.extractJwt(res);

                    // If this is the url to error out we need to replace the original token with the new one before continuing
                    // to impersonate the user, otherwise it will break when the user stops impersonating
                    if (
                        requestUrl.includes("api/v1/authorize/impersonate") &&
                        this.isImpersonating &&
                        this.#initialImpersonation
                    ) {
                        window.localStorage.setItem(
                            STORAGE_KEYS.ORIGINAL_ACCESS_TOKEN,
                            window.localStorage.getItem(
                                STORAGE_KEYS.ACCESS_TOKEN
                            )
                        );
                        window.localStorage.setItem(
                            STORAGE_KEYS.ORIGINAL_EXPIRES_AT,
                            window.localStorage.getItem(STORAGE_KEYS.EXPIRES_AT)
                        );
                        window.localStorage.setItem(
                            STORAGE_KEYS.ORIGINAL_REFRESH_TOKEN,
                            window.localStorage.getItem(
                                STORAGE_KEYS.REFRESH_TOKEN
                            )
                        );
                    }

                    return jwt;
                }),
                catchError((err: unknown) => {
                    console.log(`refresh error: ${err}`);
                    return throwError(err);
                })
            );
    }

    sendPasswordResetEmail(email: string) {
        return this.http.post(
            `${API_URL}/api/v1/password-reset`,
            JSON.stringify(email),
            {
                headers: new HttpHeaders().set(
                    "Content-Type",
                    "application/json"
                )
            }
        );
    }

    resetPassword(id: string, password: string) {
        return this.http
            .put(`${API_URL}/api/v1/password-reset`, {
                password,
                token: id
            })
            .pipe(catchError((err: unknown) => throwError(err)));
    }

    logout() {
        this.#claims = null;

        this.clearMfaPreauthorization();

        window.localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        window.localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
        window.localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        window.localStorage.removeItem(STORAGE_KEYS.ORIGINAL_ACCESS_TOKEN);
        window.localStorage.removeItem(STORAGE_KEYS.ORIGINAL_EXPIRES_AT);
        window.localStorage.removeItem(STORAGE_KEYS.ORIGINAL_REFRESH_TOKEN);
    }

    clearMfaPreauthorization() {
        window.sessionStorage.removeItem(STORAGE_KEYS.MFA_ACCESS_TOKEN);
        window.sessionStorage.removeItem(STORAGE_KEYS.MFA_EXPIRES_AT);
    }

    private setRoles(str: string[]) {
        const arr = str;
        this.#roles = [];
        arr.forEach((elem) => this.#roles.push(RoleUtil.create(elem)));
    }

    private extractJwt(res: TokenResponse) {
        this.#claims = null;

        if (res.isPreAuthorization) {
            window.sessionStorage.setItem(
                STORAGE_KEYS.MFA_ACCESS_TOKEN,
                res.accessToken
            );
            window.sessionStorage.setItem(
                STORAGE_KEYS.MFA_EXPIRES_AT,
                res.expiresAt
            );

            window.localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            window.localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
            window.localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        } else {
            window.localStorage.setItem(
                STORAGE_KEYS.ACCESS_TOKEN,
                res.accessToken
            );
            window.localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, res.expiresAt);
            window.localStorage.setItem(
                STORAGE_KEYS.REFRESH_TOKEN,
                res.refreshToken
            );

            this.clearMfaPreauthorization();
        }

        return res;
    }
}

// TODO: Simple map that's only present in this file... can we rework it to use a reverse type of Role?
//       Alternatively a switch statement is probably good enough
class RoleUtil {
    static create(str: string): Role {
        if (str === "ISI Administration") {
            return Role.ISIAdministration;
        }

        if (str === "ISI Accounting") {
            return Role.ISIAccounting;
        }

        if (str === "ISI Customer Service") {
            return Role.ISICustomerService;
        }

        if (str === "ISI Staff") {
            return Role.ISIStaff;
        }

        if (str === "ISI Claims") {
            return Role.ISIClaims;
        }

        if (str === "Agency Representative") {
            return Role.AgencyRepresentative;
        }

        if (str === "Lender Administration") {
            return Role.LenderAdministration;
        }
        if (str === "Lender Claims") {
            return Role.LenderClaims;
        }
        if (str === "Lender Accounting") {
            return Role.LenderAccounting;
        }
        if (str === "Lender View Only") {
            return Role.LenderViewOnly;
        }

        return Role.Unknown;
    }
}
